/**
 * Reports Page - Company Admin
 */
import { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { FileText, Plus, Download, Clock, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import * as api from '@/lib/companyAdminApi';

const STATUS_ICONS = {
  QUEUED: Clock,
  RUNNING: Loader2,
  SUCCEEDED: CheckCircle,
  FAILED: XCircle,
  EXPIRED: Clock,
};

const STATUS_COLORS = {
  QUEUED: 'bg-gray-100 text-gray-600',
  RUNNING: 'bg-blue-100 text-blue-600',
  SUCCEEDED: 'bg-green-100 text-green-600',
  FAILED: 'bg-red-100 text-red-600',
  EXPIRED: 'bg-orange-100 text-orange-600',
};

const REPORT_TYPES = [
  { value: 'transactions_csv', label: 'Transactions (CSV)' },
  { value: 'orders_csv', label: 'Orders (CSV)' },
  { value: 'users_csv', label: 'Users (CSV)' },
  { value: 'restaurants_csv', label: 'Restaurants (CSV)' },
  { value: 'subscriptions_csv', label: 'Subscriptions (CSV)' },
  { value: 'audit_logs_csv', label: 'Audit Logs (CSV)' },
];

export default function ReportsPage() {
  const { token } = useOutletContext();
  const [exports, setExports] = useState([]);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [reportType, setReportType] = useState('transactions_csv');
  const [creating, setCreating] = useState(false);
  // There is no export-history endpoint: the table lists only exports started in this session.
  const [pollingIds, setPollingIds] = useState(new Set());

  const handleCreateExport = async () => {
    setCreating(true);
    try {
      const response = await api.createReportExport({
        report_type: reportType,
        params: {},
      });
      const exportJob = response.data;
      setExports(prev => [exportJob, ...prev]);
      setPollingIds(prev => new Set([...prev, exportJob.id]));
      setShowCreateDialog(false);
      toast.success('Export started');
      
      // Start polling this export
      pollExport(exportJob.id);
    } catch (error) {
      toast.error('Failed to start export');
    } finally {
      setCreating(false);
    }
  };

  const pollExport = async (exportId) => {
    const maxAttempts = 30;
    let attempts = 0;
    
    const poll = async () => {
      try {
        const response = await api.getReportStatus(exportId);
        const status = response.data;
        
        setExports(prev => prev.map(e => 
          e.id === exportId ? { ...e, ...status } : e
        ));

        if (status.status === 'SUCCEEDED' || status.status === 'FAILED') {
          setPollingIds(prev => {
            const next = new Set(prev);
            next.delete(exportId);
            return next;
          });
          if (status.status === 'SUCCEEDED') {
            toast.success('Export ready for download');
          } else {
            toast.error('Export failed');
          }
          return;
        }

        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, 2000);
        }
      } catch (error) {
        console.error('Poll failed');
      }
    };
    
    poll();
  };

  const handleDownload = async (exportId) => {
    try {
      const response = await api.getReportDownload(exportId);
      const { download_url, file_url } = response.data;
      const url = download_url || file_url;
      if (url) {
        // For relative URLs, prepend the API base
        const fullUrl = url.startsWith('/') 
          ? `${process.env.REACT_APP_BACKEND_URL}${url}`
          : url;
        window.open(fullUrl, '_blank');
      }
    } catch (error) {
      toast.error('Failed to download');
    }
  };

  return (
    <div className="space-y-6" data-testid="reports-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Reports</h1>
          <p className="text-gray-500">Export data and generate reports</p>
        </div>
        <Button onClick={() => setShowCreateDialog(true)} data-testid="create-report-btn">
          <Plus className="h-4 w-4 mr-2" />
          New Export
        </Button>
      </div>

      {/* Quick Export Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {REPORT_TYPES.slice(0, 3).map((type) => (
          <Card key={type.value} className="cursor-pointer hover:border-blue-500 transition-colors"
                onClick={() => { setReportType(type.value); setShowCreateDialog(true); }}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <FileText className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="font-medium">{type.label}</p>
                  <p className="text-xs text-gray-500">Click to export</p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Exports Table */}
      <Card>
        <CardHeader>
          <CardTitle>Exports This Session</CardTitle>
          <p className="text-sm text-gray-500">Exports you start here are listed until you leave this page; past exports are not shown.</p>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Report Type</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Progress</TableHead>
                <TableHead>Created</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {exports.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8 text-gray-500">
                    No exports started in this session.
                  </TableCell>
                </TableRow>
              ) : (
                exports.map((exp) => {
                  const StatusIcon = STATUS_ICONS[exp.status] || Clock;
                  return (
                    <TableRow key={exp.id}>
                      <TableCell className="font-medium">{exp.report_type}</TableCell>
                      <TableCell>
                        <Badge className={STATUS_COLORS[exp.status]}>
                          <StatusIcon className={`h-3 w-3 mr-1 ${exp.status === 'RUNNING' ? 'animate-spin' : ''}`} />
                          {exp.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="w-[150px]">
                        <Progress value={exp.progress || 0} className="h-2" />
                      </TableCell>
                      <TableCell className="text-sm text-gray-500">
                        {exp.created_at ? new Date(exp.created_at).toLocaleString() : 'Just now'}
                      </TableCell>
                      <TableCell className="text-right">
                        {exp.status === 'SUCCEEDED' && (
                          <Button size="sm" variant="outline" onClick={() => handleDownload(exp.id)}>
                            <Download className="h-4 w-4 mr-1" />
                            Download
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Export</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Report Type</label>
              <Select value={reportType} onValueChange={setReportType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {REPORT_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateExport} disabled={creating}>
              {creating ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Starting...
                </>
              ) : (
                'Start Export'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
